<script setup lang="ts">
/**
 * **端口** —— `<Handle>` 的替代品（我们自己的画布）。
 *
 * ## 它必须满足的两件事
 *
 * ① **DOM 契约**（`graphDom.ts` 的层②）：
 *    引擎要提供 `.np-graph__handle`，并在上面挂
 *    `data-nodeid` / `data-handleid` / `data-handlepos` ——
 *    全仓库有 5 处按这些属性找端口（命中测试、悬停提示、连线手势、
 *    拉线改接）。属性名和拼写都照契约来，**一个字母都不能差**。
 *
 * ② **盒子与端口圆点重合**：连线端点由"量这个元素的中心"得到，
 *    所以盒子必须正好等于 `NpSocket` 的尺寸（`--np-socket-size`）——
 *    否则线会接到端口**旁边**（差半个圆点看着像"没对齐"）。
 *    样式在 `np-canvas.css` 里，和 `NpSocket` 共用同一个令牌。
 *
 * ## 和原来 `<Handle>` 的差别（都是有意为之）
 *
 * · **不注册到任何 store**：位置靠 `updateNodeInternals` 扫 DOM 得到
 *   （见 `handleGeometry.ts`）。少一套"注册 / 反注册"的生命周期，
 *   也就少一类"组件卸载了但注册表里还留着"的 bug。
 * · **`connectable=false` 的端口不接指针事件**（折叠分组的锚点用它）：
 *   原来靠 CSS + 引擎的 `connectable` 两处约束，现在只剩 CSS 一处，
 *   所以这里**同时**写 `data-connectable` 给画布看（它据此决定要不要起线）。
 */
withDefaults(
  defineProps<{
    /** 节点 id（契约属性 `data-nodeid`） */
    nodeId: string
    /** 端口 id（契约属性 `data-handleid`） */
    id: string
    /** 输入还是输出（契约属性 `data-handlepos` 由 `position` 决定） */
    type: 'target' | 'source'
    /** 在节点的哪一侧 —— 决定 CSS 类名 `--left` / `--right` */
    position: 'left' | 'right'
    /** 能不能从这里起线（锚点给 `false`） */
    connectable?: boolean
  }>(),
  { connectable: true },
)
</script>

<template>
  <div
    class="np-graph__handle"
    :class="[
      `np-graph__handle--${position}`,
      type === 'target' ? 'is-target' : 'is-source',
      { 'is-locked': !connectable },
    ]"
    :data-nodeid="nodeId"
    :data-handleid="id"
    :data-handlepos="position"
    :data-connectable="connectable ? undefined : 'false'"
  >
    <slot />
  </div>
</template>
